import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import {
  formatIsoDate,
  renderStatus,
  formatName,
} from "../../utils/generalUtils";
import GeneratePdf from "../../generatePdf/GeneratePdf";
import { Select, Space } from "antd";
import SpectatorTable from "../../components/Table/SpectatorTable.js";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatAbsentDatesLabel(absentDates) {
  if (!Array.isArray(absentDates) || absentDates.length === 0) return "None";
  // New submissions store a single free-text string; old ones store ISO date strings.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(absentDates[0])) {
    return absentDates[0] || "None";
  }
  return absentDates
    .map((ds) => {
      const d = new Date(ds + "T00:00:00");
      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    })
    .join(", ");
}

function formatAccomplishmentsLabel(accomplishments) {
  if (!Array.isArray(accomplishments) || accomplishments.length === 0)
    return "—";
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {accomplishments.map((a, i) => <li key={i}>{a}</li>)}
    </ul>
  );
}

const SpectateCwa = () => {
  const { response, loading, fetchData } = useAxios();
  const { spectatorCwaPopup } = useSelector((state) => state.generalSlice);
  const { spectator_accesses } = useSelector(
    (state) => state.accountDetailSlice.details
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/cwa/${positionId}`, method: "GET" });
  };

  const mappedEmpPos = (spectator_accesses || []).map((item) => ({
    value: item?.approver_position_id,
    label: item?.approver_position
      ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
      : `Position ${item?.approver_position_id}`,
  }));
  const hasActiveSpectatorPosition = mappedEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    mappedEmpPos.length > 0 ? mappedEmpPos[0].value : undefined
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      mappedEmpPos.length > 0
    ) {
      setSelectedPos(mappedEmpPos[0].value);
    }
  }, [mappedEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) return;
    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      approver_position_name: item?.position?.position_name ?? "",
      approver_position_unit: item?.position?.unit ?? "",
      submitter_position: item?.submission?.position?.position_name,
      submitter_position_unit: item?.submission?.position?.unit,
      submitter_email: item?.submission?.submitter?.email,
      ...item?.submission?.certificate_of_work_accomplishment,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", render: (val) => val || "—" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <GeneratePdf
          docuType="cwa"
          contents={item}
          disabled={item?.status !== "approved"}
        />
      ),
    },
  ];

  const viewingDataConfig = [
    {
      key: 1,
      collapseName: "Period",
      collapseItems: [
        { key: 1, name: "start_date", label: "Start Date" },
        { key: 2, name: "end_date", label: "End Date" },
        {
          key: 3,
          name: "absent_dates",
          label: "Dates of Absence",
          render: (val) => formatAbsentDatesLabel(val),
        },
      ],
    },
    {
      key: 2,
      collapseName: "Accomplishments",
      collapseItems: [
        {
          key: 1,
          name: "accomplishments",
          label: "Accomplishments",
          render: (val) => formatAccomplishmentsLabel(val),
        },
      ],
    },
    {
      key: 3,
      collapseName: "Remarks",
      collapseItems: [
        { key: 1, name: "remarks", label: "Remarks", render: (val) => val || "None" },
      ],
    },
  ];

  return (
    <>
      {loading && "loading..."}
      {!loading && (
        <>
          <Space>
            <p>Spectate: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveSpectatorPosition}
            />
          </Space>
          {!hasActiveSpectatorPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active spectator position available.
            </p>
          )}
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={spectatorCwaPopup}
            popupName="spectatorCwaPopup"
            docuType="Certificate of Work Accomplishment"
            viewingDataConfig={viewingDataConfig}
          />
        </>
      )}
    </>
  );
};

export default SpectateCwa;
